import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box, Typography, Button, List, ListItem, ListItemText,
  Paper, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, Tab, Tabs, Grid,
  Card, CardContent, CardActions, Collapse, Switch, FormControlLabel, Select, MenuItem, FormControl, InputLabel, Chip, Alert, AlertTitle
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BlockIcon from '@mui/icons-material/Block';
import CircularProgress from '@mui/material/CircularProgress';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip as ChartTooltip, ResponsiveContainer
} from 'recharts';
import { getApiKeys, createApiKey, deleteApiKey, revokeApiKey, updateApiKey, updateApiKeyDomains, setApiKey, setApiEndpoint } from '../../api/services/agent-studio/AgentFeatures';

const UsageChart = ({ usage }) => {
  const theme = useTheme();
  const movingAverage = usage.map((day, idx, arr) => {
    const window = arr.slice(Math.max(0, idx - 6), idx + 1);
    const avg = window.reduce((sum, d) => sum + d.requests, 0) / window.length;
    return { ...day, average: Math.round(avg) };
  });

  return (
    <Box sx={{ height: 300, mt: 2 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart 
          data={movingAverage} 
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis 
            dataKey="date" 
            tickFormatter={(date) => new Date(date).toLocaleDateString()}
            style={{ fontSize: '0.75rem' }}
            stroke={theme.palette.text.secondary}
          />
          <YAxis 
            style={{ fontSize: '0.75rem' }} 
            stroke={theme.palette.text.secondary}
            tickFormatter={(value) => value.toLocaleString()}
          />
          <ChartTooltip 
            contentStyle={{ 
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(33, 33, 33, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              border: 'none',
              borderRadius: 8,
              boxShadow: theme.shadows[4],
              color: theme.palette.text.primary
            }}
            formatter={(value, name) => [
              `${value.toLocaleString()} requests`, 
              name === 'average' ? '7-day Average' : 'Daily Usage'
            ]}
            labelFormatter={(label) => new Date(label).toLocaleDateString()}
          />
          <Line 
            type="monotone" 
            dataKey="requests" 
            stroke={theme.palette.info.main}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6 }}
          />
          <Line 
            type="monotone" 
            dataKey="average" 
            stroke={theme.palette.success.main}
            strokeWidth={2}
            dot={false}
            strokeDasharray="5 5"
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

const KeyDialog = ({ open, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    role: 'user',
    expiry: 30,
    rate_limit: 50
  });

  const handleChange = (field) => (event) => {
    let value = event.target.value;
    
    // Handle numeric fields
    if (field === 'expiry' || field === 'rate_limit') {
      // Remove leading zeros
      value = value.replace(/^0+/, '');
      // If empty or invalid, use empty string instead of converting to 0
      value = value === '' ? '' : parseInt(value, 10);
      
      // Additional validation for rate_limit
      if (field === 'rate_limit' && value !== '') {
        if (value > 50) value = 50;
        if (value < 0) value = 0;
      }
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isValid = () => {
    return formData.name.trim() !== '' && 
           formData.expiry > 0 && 
           Number.isInteger(formData.expiry) &&
           formData.rate_limit > 0 && 
           formData.rate_limit <= 50 &&
           Number.isInteger(formData.rate_limit);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New API Key</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            type="text"
            fullWidth
            value={formData.name}
            onChange={handleChange('name')}
            helperText="Provide a name for this API key"
            required
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              onChange={handleChange('role')}
              label="Role"
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Expiry (days)"
            type="number"
            fullWidth
            value={formData.expiry}
            onChange={handleChange('expiry')}
            InputProps={{
              inputProps: { 
                min: 1,
                step: 1 // Ensures only integer values
              }
            }}
            helperText="Must be a positive integer"
          />
          <TextField
            label="Daily Rate Limit"
            type="number"
            fullWidth
            value={formData.rate_limit}
            onChange={handleChange('rate_limit')}
            InputProps={{
              inputProps: { 
                min: 1,
                max: 50,
                step: 1
              }
            }}
            helperText="Must be a positive integer (max 50)"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={() => onSave(formData)} 
          variant="contained"
          disabled={!isValid()}
        >
          Create Key
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DomainDialog = ({ open, onClose, onSave, keyData }) => {
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');

  useEffect(() => {
    if (keyData && keyData.whitelisted_domains) {
      setDomains(keyData.whitelisted_domains);
    } else {
      setDomains([]);
    }
  }, [keyData]);

  const handleAddDomain = () => {
    if (newDomain.trim() && !domains.includes(newDomain.trim())) {
      setDomains([...domains, newDomain.trim()]);
      setNewDomain('');
    }
  };

  const handleRemoveDomain = (domainToRemove) => {
    setDomains(domains.filter(domain => domain !== domainToRemove));
  };

  const handleSave = () => {
    onSave(keyData.id, domains);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Whitelisted Domains</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure which domains are allowed to use this API key.
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            label="Domain"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder="example.com"
            fullWidth
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddDomain();
              }
            }}
          />
          <Button 
            variant="contained" 
            onClick={handleAddDomain}
            disabled={!newDomain.trim()}
          >
            Add
          </Button>
        </Box>

        {domains.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Whitelisted Domains:
            </Typography>
            {domains.map((domain, index) => (
              <Chip
                key={index}
                label={domain}
                onDelete={() => handleRemoveDomain(domain)}
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>
        )}

        {domains.length === 0 && (
          <Alert severity="info">
            No domains configured. The API key will work with all domains.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const KeyItem = ({ keyData, onDelete, onManageDomains }) => {
  return (
    <ListItem
      sx={{ 
        borderBottom: '1px solid',
        borderColor: 'divider',
        py: 2
      }}
    >
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1">
              {keyData.name || 'Unnamed Key'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {keyData.id}
            </Typography>
            <Chip 
              label={keyData.role}
              color={keyData.role === 'admin' ? 'primary' : 'default'}
              size="small"
            />
            {!keyData.is_active && (
              <Chip label="Inactive" color="error" size="small" />
            )}
          </Box>
        }
        secondary={
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Rate Limit: {keyData.rate_limit || 'N/A'} requests/day
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Created: {new Date(keyData.created_at).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Expires: {new Date(keyData.expires_at).toLocaleString()}
            </Typography>
            {keyData.whitelisted_domains && keyData.whitelisted_domains.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Whitelisted Domains:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {keyData.whitelisted_domains.slice(0, 3).map((domain, index) => (
                    <Chip 
                      key={index}
                      label={domain} 
                      size="small" 
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  ))}
                  {keyData.whitelisted_domains.length > 3 && (
                    <Chip 
                      label={`+${keyData.whitelisted_domains.length - 3} more`}
                      size="small" 
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        }
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Tooltip title="Manage Domains">
          <IconButton onClick={() => onManageDomains(keyData)} size="small">
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete Key">
          <IconButton onClick={() => onDelete(keyData.id)} size="small">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </ListItem>
  );
};

const KeyManager = ({ apiKey, agentId, endpoint }) => {
  const { serverId } = useParams();
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedServer, setSelectedServer] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newKeyData, setNewKeyData] = useState(null);
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [selectedKeyForDomains, setSelectedKeyForDomains] = useState(null);
  
  useEffect(() => {
    // Set the current agent's API key for the services
    if (apiKey) {
      setApiKey(apiKey);
      if (endpoint) {
        setApiEndpoint(endpoint);
      }
      fetchKeys();
    } else {
      setError('No API key available for this agent');
      setLoading(false);
    }
  }, [apiKey, endpoint]);

  const fetchKeys = async () => {
    try {
      setLoading(true);
      if (!apiKey) {
        throw new Error('API key is missing');
      }
      
      const response = await getApiKeys(apiKey, endpoint);
      // Extract the keys array from the nested response structure
      const keysArray = response?.data?.keys || [];
      setKeys(keysArray);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      setError('Failed to load API keys. Please try again later.');
      setKeys([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async (formData) => {
    try {
      if (!apiKey) {
        throw new Error('API key is missing');
      }
      
      const response = await createApiKey(formData, apiKey, endpoint);
      setNewKeyData(response.api_key);
      await fetchKeys(); // Refresh the list
      setDialogOpen(false);
    } catch (error) {
      console.error('Failed to create API key:', error);
      setError('Failed to create API key. Please try again.');
    }
  };

  const handleDeleteKey = async (keyId) => {
    try {
      if (!apiKey) {
        throw new Error('API key is missing');
      }
      
      await deleteApiKey(keyId, apiKey, endpoint);
      await fetchKeys();
    } catch (error) {
      console.error('Failed to delete API key:', error);
      setError('Failed to delete API key. Please try again.');
    }
  };

  const handleCopyKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      // Optional: You could add a state to show a "Copied!" message
    } catch (error) {
      console.error('Failed to copy key:', error);
    }
  };

  const handleManageDomains = (keyData) => {
    setSelectedKeyForDomains(keyData);
    setDomainDialogOpen(true);
  };

  const handleUpdateDomains = async (keyId, whitelistedDomains) => {
    try {
      if (!apiKey) {
        throw new Error('API key is missing');
      }
      
      await updateApiKeyDomains(keyId, whitelistedDomains, apiKey, endpoint);
      await fetchKeys(); // Refresh the list to show updated domains
      setDomainDialogOpen(false);
      setSelectedKeyForDomains(null);
    } catch (error) {
      console.error('Failed to update API key domains:', error);
      setError('Failed to update API key domains. Please try again.');
    }
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      overflow: 'hidden',
      p: 2
    }}>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">API Keys</Typography>
          <Button
            variant="contained"
            onClick={() => setDialogOpen(true)}
            startIcon={<AddIcon />}
          >
            Create New Key
          </Button>
        </Box>
        
        {newKeyData && (
          <Alert 
            severity="success" 
            sx={{ mb: 2 }}
            onClose={() => setNewKeyData(null)}
          >
            <AlertTitle>API Key Created Successfully</AlertTitle>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              bgcolor: 'rgba(0, 0, 0, 0.04)',
              p: 1,
              borderRadius: 1,
              my: 1
            }}>
              <Typography 
                component="code" 
                sx={{ 
                  flexGrow: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.9rem'
                }}
              >
                {newKeyData}
              </Typography>
              <IconButton 
                size="small"
                onClick={() => handleCopyKey(newKeyData)}
                title="Copy to clipboard"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body2">
              Make sure to copy this key now. You won't be able to see it again!
            </Typography>
          </Alert>
        )}
      </Box>

      <Paper sx={{ 
        flexGrow: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography variant="body1" color="error" sx={{ p: 2 }}>{error}</Typography>
        ) : (
          <List sx={{ 
            flexGrow: 1,
            overflow: 'auto',
            '& > :last-child': {
              borderBottom: 'none'
            }
          }}>
            {keys.map((key) => (
              <KeyItem
                key={key.id}
                keyData={key}
                onDelete={handleDeleteKey}
                onManageDomains={handleManageDomains}
              />
            ))}
          </List>
        )}
      </Paper>

      <KeyDialog 
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleCreateKey}
      />

      <DomainDialog 
        open={domainDialogOpen}
        onClose={() => {
          setDomainDialogOpen(false);
          setSelectedKeyForDomains(null);
        }}
        onSave={handleUpdateDomains}
        keyData={selectedKeyForDomains}
      />
    </Box>
  );
};

export default KeyManager;
